import { lazy as reactLazy, type ComponentType } from "react";
import { isWebview, webviewPostMessage } from "@/utils/webviewUtil";

export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? "unknown";

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Loading chunk",
  "Unable to preload CSS",
  "Failed to load module script",
  "is not a valid JavaScript MIME type",
];

export function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;

  const message = error.message || "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

const RELOAD_COUNT_KEY = "chunk_error_reload_count";
const RELOAD_TS_KEY = "chunk_error_reload_ts";
const RELOAD_VERSION_KEY = "chunk_error_reload_version";
const MAX_RELOADS = 2;
const RESET_INTERVAL = 30000;

function clearCacheAndReload() {
  if (isWebview()) {
    webviewPostMessage("clearWebsiteData", { type: ["cache"] });
    setTimeout(() => window.location.reload(), 300);
  } else {
    window.location.reload();
  }
}

export function attemptChunkRecoveryReload(): boolean {
  const storedVersion = sessionStorage.getItem(RELOAD_VERSION_KEY);
  const lastTs = parseInt(sessionStorage.getItem(RELOAD_TS_KEY) || "0", 10);
  const now = Date.now();

  let reloadCount = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0", 10);

  if (storedVersion !== APP_VERSION || now - lastTs > RESET_INTERVAL) {
    reloadCount = 0;
  }

  if (reloadCount < MAX_RELOADS) {
    sessionStorage.setItem(RELOAD_COUNT_KEY, (reloadCount + 1).toString());
    sessionStorage.setItem(RELOAD_TS_KEY, now.toString());
    sessionStorage.setItem(RELOAD_VERSION_KEY, APP_VERSION);
    clearCacheAndReload();
    return true;
  }

  sessionStorage.removeItem(RELOAD_COUNT_KEY);
  sessionStorage.removeItem(RELOAD_TS_KEY);
  sessionStorage.removeItem(RELOAD_VERSION_KEY);
  return false;
}

function retryImport<T>(importFn: () => Promise<T>, retries: number, delay: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const attempt = (remaining: number) => {
      importFn()
        .then(resolve)
        .catch((error) => {
          if (isChunkLoadError(error) && remaining > 0) {
            setTimeout(() => attempt(remaining - 1), delay);
          } else {
            reject(error);
          }
        });
    };
    attempt(retries);
  });
}

export function lazyLoad<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: { retries?: number; delay?: number } = {},
) {
  const { retries = 1, delay = 500 } = options;
  return reactLazy(() => retryImport(importFn, retries, delay));
}
